import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, Input, Label, Textarea } from '../components/ui/primitives';

type BillingFormState = {
  legalEntityName: string;
  billingAddress: string;
  accountsPayableEmail: string;
  registrationNumber: string;
  vatNumber: string;
  poNumber: string;
  costCentreNumber: string;
};

type LandingBillingAccess = {
  orderId: string;
  expiresAt: string;
  requesterName: string | null;
  requesterEmail: string | null;
  requesterCompanyName: string | null;
  offering: {
    code: string;
    name: string;
    category: string;
    buildType: string | null;
    amountZar: string;
  };
  buildRequest: {
    id: string;
    roleName: string;
    notes: string | null;
    status: string;
  } | null;
  billingSubmitted: boolean;
};

type BillingSubmitResponse = {
  orderId: string;
  paymentStatus: string;
  fulfillmentStatus: string;
  message: string;
  paymentUrl?: string | null;
};

const EMPTY_FORM: BillingFormState = {
  legalEntityName: '',
  billingAddress: '',
  accountsPayableEmail: '',
  registrationNumber: '',
  vatNumber: '',
  poNumber: '',
  costCentreNumber: '',
};

const getApiBaseUrl = () => (import.meta.env.VITE_API_URL || 'http://localhost:5001/api').replace(/\/+$/, '');

const buildBillingApiUrl = (token: string) =>
  `${getApiBaseUrl()}/public/build-requests/billing/${encodeURIComponent(token)}`;

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const requestJson = async <T,>(url: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(url, {
    ...init,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  });
  const body = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      typeof body?.error?.message === 'string' && body.error.message.trim()
        ? body.error.message
        : 'Request failed.';
    throw new Error(message);
  }

  return body as T;
};

const PublicBuildRequestBillingPage: React.FC = () => {
  const { token = '' } = useParams();
  const billingApiUrl = useMemo(() => (token ? buildBillingApiUrl(token) : ''), [token]);
  const [access, setAccess] = useState<LandingBillingAccess | null>(null);
  const [formState, setFormState] = useState<BillingFormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitResult, setSubmitResult] = useState<BillingSubmitResponse | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setLoadError(null);

      try {
        if (!billingApiUrl) {
          throw new Error('Billing link token is missing.');
        }
        const result = await requestJson<LandingBillingAccess>(billingApiUrl);
        if (!mounted) return;
        setAccess(result);
        setFormState({
          legalEntityName: result.requesterCompanyName || '',
          billingAddress: '',
          accountsPayableEmail: result.requesterEmail || '',
          registrationNumber: '',
          vatNumber: '',
          poNumber: '',
          costCentreNumber: '',
        });
      } catch (error) {
        if (!mounted) return;
        setLoadError(getErrorMessage(error, 'Unable to load this billing link right now.'));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [billingApiUrl]);

  const updateField = (field: keyof BillingFormState, value: string) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
    }));
    if (submitError) setSubmitError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setSubmitError(null);

    try {
      const result = await requestJson<BillingSubmitResponse>(billingApiUrl, {
        method: 'POST',
        body: JSON.stringify({
          legalEntityName: formState.legalEntityName.trim(),
          billingAddress: formState.billingAddress.trim(),
          accountsPayableEmail: formState.accountsPayableEmail.trim(),
          registrationNumber: formState.registrationNumber.trim(),
          vatNumber: formState.vatNumber.trim(),
          poNumber: formState.poNumber.trim(),
          costCentreNumber: formState.costCentreNumber.trim(),
        }),
      });
      setSubmitResult(result);
    } catch (error) {
      setSubmitError(getErrorMessage(error, 'Unable to save billing details right now.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8faf9] font-body text-[#0f172a]">
      <header className="flex h-14 w-full items-center bg-[#0d9488] px-6 sm:px-8">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-white/20 text-[19px] font-bold text-white">
            I
          </div>
          <p className="font-display text-[22px] font-bold tracking-[0.04em] text-white sm:text-[24px]">
            INTELLERVIEW
          </p>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[880px] space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        <section className="rounded-[18px] border border-[#dbe4e1] bg-[linear-gradient(135deg,#ffffff_0%,#f0fdfa_100%)] px-5 py-5 shadow-[0_18px_40px_rgba(15,23,42,0.06)] sm:px-7 sm:py-6">
          <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-[#0d9488]">Billing Details</p>
          <h1 className="mt-2 font-display text-[28px] font-bold leading-tight text-[#0f172a] sm:text-[34px]">
            Complete your invoice details
          </h1>
          <p className="mt-2 max-w-[640px] text-[14px] leading-6 text-[#475569]">
            We need these details before issuing your invoice and PayFast payment link.
          </p>
        </section>

        {loadError ? (
          <Card className="border-[#fde68a] bg-[#fffbeb]">
            <p className="text-[14px] font-medium text-[#92400e]">{loadError}</p>
          </Card>
        ) : null}

        {!loadError && access ? (
          <Card className="border-[#dbe4e1] bg-[#fcfffe]">
            <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#64748b]">Current request</p>
            <p className="mt-2 text-[16px] font-semibold text-[#0f172a]">
              {access.buildRequest?.roleName || 'Build request'}
            </p>
            <p className="mt-1 text-[14px] text-[#64748b]">
              {access.requesterCompanyName || 'Your company'} · {access.offering.name} · R{access.offering.amountZar}
            </p>
          </Card>
        ) : null}

        <Card className="border-[#dbe4e1]">
          <CardHeader>
            <h2 className="font-display text-[24px] font-bold text-[#0f172a]">Where should we send the invoice?</h2>
            <p className="mt-1 text-[14px] text-[#64748b]">
              Your invoice and PayFast payment link will be sent after these details are saved.
            </p>
          </CardHeader>
          <CardBody>
            {loading ? <p className="text-[14px] text-[#64748b]">Loading billing link...</p> : null}

            {submitResult ? (
              <div className="space-y-4">
                <Card className="border-[#bbf7d0] bg-[#f0fdf4]">
                  <p className="text-[14px] font-semibold text-[#166534]">
                    Billing details saved. We’ll issue your invoice and email your PayFast payment link shortly.
                  </p>
                </Card>
                {submitResult.paymentUrl ? (
                  <a className="iv-btn-primary inline-flex" href={submitResult.paymentUrl}>
                    Pay by PayFast
                  </a>
                ) : null}
              </div>
            ) : null}

            {!loading && !loadError && !submitResult ? (
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <Label htmlFor="publicCompanyName">Company name</Label>
                  <Input
                    id="publicCompanyName"
                    value={formState.legalEntityName}
                    onChange={(event) => updateField('legalEntityName', event.target.value)}
                    placeholder="Your company name"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="publicInvoiceEmail">Email for invoice</Label>
                  <Input
                    id="publicInvoiceEmail"
                    type="email"
                    value={formState.accountsPayableEmail}
                    onChange={(event) => updateField('accountsPayableEmail', event.target.value)}
                    placeholder="finance@company.com"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="publicBillingAddress">Billing address</Label>
                  <Textarea
                    id="publicBillingAddress"
                    value={formState.billingAddress}
                    onChange={(event) => updateField('billingAddress', event.target.value)}
                    placeholder="Street, suburb, city, postcode, country"
                    rows={5}
                    required
                  />
                </div>

                <details className="rounded-[14px] border border-[#e2e8f0] bg-[#fcfffe] px-4 py-4">
                  <summary className="cursor-pointer text-[14px] font-semibold text-[#0f172a]">
                    Add optional finance references
                  </summary>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="publicRegistrationNumber">Registration number</Label>
                      <Input
                        id="publicRegistrationNumber"
                        value={formState.registrationNumber}
                        onChange={(event) => updateField('registrationNumber', event.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="publicVatNumber">VAT number</Label>
                      <Input
                        id="publicVatNumber"
                        value={formState.vatNumber}
                        onChange={(event) => updateField('vatNumber', event.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="publicPoNumber">Purchase order number</Label>
                      <Input
                        id="publicPoNumber"
                        value={formState.poNumber}
                        onChange={(event) => updateField('poNumber', event.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="publicCostCentreNumber">Cost centre</Label>
                      <Input
                        id="publicCostCentreNumber"
                        value={formState.costCentreNumber}
                        onChange={(event) => updateField('costCentreNumber', event.target.value)}
                      />
                    </div>
                  </div>
                </details>

                {submitError ? <p className="text-[13px] font-medium text-[#b91c1c]">{submitError}</p> : null}

                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Save billing details'}
                </Button>
              </form>
            ) : null}
          </CardBody>
        </Card>
      </main>
    </div>
  );
};

export default PublicBuildRequestBillingPage;
